import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Printer, RefreshCw, Search, SlidersHorizontal, X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import {
  FETCH_CHUNK,
  REPORT_TABLE_HEADER_BG,
  REPORT_TABLE_HEADER_TEXT,
  REPORT_TABLE_HEADER_BORDER,
  applyExcelHeaderRowStyle,
  printHtmlInSameTab,
} from './reportPrintHelpers';

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function defaultFilters() {
  return {
    dateFrom: todayInput(),
    dateTo: todayInput(),
    isPickupDate: false,
    isCreatedDate: false,
    voucherNo: '',
    billNo: '',
    keyword: '',
    isPaid: false,
    isNotPaid: false,
    statusIds: [],
    areaIds: [],
  };
}

function idName(x) {
  return {
    id: x.Id ?? x.id,
    name: x.Name ?? x.name ?? '',
  };
}

function buildPayload(skip, take, f) {
  const d0 = new Date(f.dateFrom + 'T12:00:00');
  const d1 = new Date(f.dateTo + 'T12:00:00');
  return {
    Skip: skip,
    Take: take,
    FromYear: d0.getFullYear(),
    FromMonth: d0.getMonth() + 1,
    FromDay: d0.getDate(),
    ToYear: d1.getFullYear(),
    ToMonth: d1.getMonth() + 1,
    ToDay: d1.getDate(),
    IsPickupDate: !!f.isPickupDate,
    IsCreatedDate: !!f.isCreatedDate,
    VoucherNo: (f.voucherNo || '').trim(),
    BillNo: (f.billNo || '').trim(),
    Keyword: (f.keyword || '').trim(),
    StatusIds: f.statusIds.length ? f.statusIds : null,
    AreaIds: f.areaIds.length ? f.areaIds : null,
    IsPaid: !!f.isPaid,
    IsNotPaid: !!f.isNotPaid,
    ClientId: 0,
  };
}

function formatDate(d) {
  if (d == null || d === '') return '—';
  if (typeof d === 'string') {
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
    const dt = new Date(d.Year, d.Month - 1, d.Day);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function num(n) {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  return Number.isNaN(v) ? '—' : v.toFixed(2);
}

function parseNumberField(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function computeNumericSums(orderRows) {
  const sums = {
    Quantity: 0,
    ProductPrice: 0,
    DeliveryCharge: 0,
    TAX: 0,
    CustomerAmount: 0,
    ClientAmount: 0,
  };
  for (const r of orderRows) {
    sums.Quantity += parseNumberField(r.Quantity ?? r.quantity);
    sums.ProductPrice += parseNumberField(r.ProductPrice ?? r.productPrice);
    sums.DeliveryCharge += parseNumberField(r.DeliveryCharge ?? r.deliveryCharge);
    sums.TAX += parseNumberField(r.TAX ?? r.tax);
    sums.CustomerAmount += parseNumberField(r.CustomerAmount ?? r.customerAmount);
    sums.ClientAmount += parseNumberField(r.ClientAmount ?? r.clientAmount);
  }
  return sums;
}

function formatSumQuantity(n) {
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return n.toFixed(2);
}

function rowToFlat(r, index) {
  const client = r.Client ?? r.client;
  const customer = r.Customer ?? r.customer;
  const status = r.Status ?? r.status;
  return {
    '#': index + 1,
    VoucherNo: r.VoucherNo ?? r.voucherNo ?? '',
    ClientBillNo: r.ClientBillNo ?? r.clientBillNo ?? '',
    DeliveryDate: formatDate(r.DeliveryDate ?? r.deliveryDate),
    CreatedDate: formatDate(r.Date ?? r.date),
    ProductPrice: r.ProductPrice ?? r.productPrice ?? '',
    DeliveryCharge: r.DeliveryCharge ?? r.deliveryCharge ?? '',
    TAX: r.TAX ?? r.tax ?? '',
    CustomerAmount: r.CustomerAmount ?? r.customerAmount ?? '',
    ClientAmount: r.ClientAmount ?? r.clientAmount ?? '',
    PayType: r.PayType ?? r.payType ?? '',
    Quantity: r.Quantity ?? r.quantity ?? '',
    Client: client?.Name ?? client?.name ?? '',
    ClientMobile: client?.MobileNo1 ?? client?.mobileNo1 ?? '',
    Customer: customer?.Name ?? customer?.name ?? '',
    CustomerMobile: customer?.MobileNo1 ?? customer?.mobileNo1 ?? '',
    Status: status?.Name ?? status?.name ?? '',
    Remarks: r.Remarks ?? r.remarks ?? '',
    PaymentRefNo: r.PaymentRefNo ?? r.paymentRefNo ?? '',
    ReturnRefNo: r.ReturnRefNo ?? r.returnRefNo ?? '',
  };
}

function rowMatchesTableSearch(r, q) {
  if (!q) return true;
  const client = r.Client ?? r.client;
  const customer = r.Customer ?? r.customer;
  const status = r.Status ?? r.status;
  const hay = [
    r.VoucherNo,
    r.voucherNo,
    r.ClientBillNo,
    r.clientBillNo,
    r.PaymentRefNo,
    r.paymentRefNo,
    r.ReturnRefNo,
    r.returnRefNo,
    r.Remarks,
    r.remarks,
    r.PayType,
    r.payType,
    r.Quantity,
    r.quantity,
    client?.Name,
    client?.name,
    client?.MobileNo1,
    client?.mobileNo1,
    customer?.Name,
    customer?.name,
    customer?.MobileNo1,
    customer?.mobileNo1,
    status?.Name,
    status?.name,
  ]
    .map((x) => String(x ?? '').toLowerCase())
    .join(' ');
  return hay.includes(q);
}

/** Column keys must match `rowToFlat` output keys (used for print / PDF column picker). */
const PRINT_COLUMN_DEFS = [
  { key: '#', label: '#' },
  { key: 'VoucherNo', label: 'Voucher' },
  { key: 'ClientBillNo', label: 'Client bill' },
  { key: 'DeliveryDate', label: 'Delivery' },
  { key: 'CreatedDate', label: 'Created' },
  { key: 'ProductPrice', label: 'Product' },
  { key: 'DeliveryCharge', label: 'Del. chg' },
  { key: 'TAX', label: 'Tax' },
  { key: 'CustomerAmount', label: 'Cust amt' },
  { key: 'ClientAmount', label: 'Client amt' },
  { key: 'PayType', label: 'Pay type' },
  { key: 'Quantity', label: 'Qty' },
  { key: 'Client', label: 'Client' },
  { key: 'ClientMobile', label: 'Client mob' },
  { key: 'Customer', label: 'Customer' },
  { key: 'CustomerMobile', label: 'Cust mob' },
  { key: 'Status', label: 'Status' },
  { key: 'Remarks', label: 'Remarks' },
  { key: 'PaymentRefNo', label: 'Pay ref' },
  { key: 'ReturnRefNo', label: 'Return ref' },
];

function defaultPrintColumnSelection() {
  return Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, true]));
}

function filterSummary(f) {
  const parts = [`${f.dateFrom} → ${f.dateTo}`];
  if (f.isPickupDate) parts.push('pickup date');
  else if (f.isCreatedDate) parts.push('created date');
  else parts.push('delivery date');
  if (f.statusIds.length) parts.push(`${f.statusIds.length} status(es)`);
  if (f.areaIds.length) parts.push(`${f.areaIds.length} area(s)`);
  if (f.voucherNo) parts.push(`voucher ${f.voucherNo}`);
  if (f.billNo) parts.push(`bill ${f.billNo}`);
  if (f.keyword) parts.push(`keyword`);
  if (f.isPaid) parts.push('paid');
  if (f.isNotPaid) parts.push('not paid');
  return parts.join(' · ');
}

export default function OrderReport() {
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const [draft, setDraft] = useState(() => defaultFilters());
  const [filterModalOpen, setFilterModalOpen] = useState(true);

  const [statuses, setStatuses] = useState([]);
  const [areas, setAreas] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  /** While loading all pages: how many rows received vs server total (for progress bar). */
  const [loadProgress, setLoadProgress] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [printPreparing, setPrintPreparing] = useState(false);
  const [printColumnModalOpen, setPrintColumnModalOpen] = useState(false);
  const [printColumnSelection, setPrintColumnSelection] = useState(defaultPrintColumnSelection);
  const [error, setError] = useState('');

  const loadLookups = useCallback(async () => {
    try {
      const [st, ar] = await Promise.all([MainService.StatusInDropDown(), MainService.AreasInDropDown()]);
      const sList = Array.isArray(st) ? st : [];
      const aList = Array.isArray(ar) ? ar : [];
      setStatuses(sList.map(idName));
      setAreas(aList.map(idName));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  /** When modal opens, copy applied filters into draft for editing. */
  useEffect(() => {
    if (filterModalOpen) {
      setDraft({ ...appliedFilters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync draft when modal is shown
  }, [filterModalOpen]);

  /** Loads every row for the filter set, one API chunk at a time, into a single scrollable list. */
  const loadReport = async (f = appliedFilters) => {
    setLoading(true);
    setHasLoaded(false);
    setLoadProgress({ loaded: 0, total: 0 });
    setRows([]);
    setTotal(0);
    setError('');
    try {
      let skip = 0;
      let serverTotal = 0;
      const accumulated = [];
      for (;;) {
        const body = buildPayload(skip, FETCH_CHUNK, f);
        const res = await MainService.OrderReportV2(body);
        const chunk = res?.Data ?? res?.data ?? [];
        const arr = Array.isArray(chunk) ? chunk : [];
        serverTotal = Number(res?.Total ?? res?.total ?? 0) || 0;
        accumulated.push(...arr);
        setRows([...accumulated]);
        setTotal(serverTotal);
        setLoadProgress({ loaded: accumulated.length, total: serverTotal });
        if (arr.length === 0) break;
        skip += arr.length;
        if (skip >= serverTotal) break;
        if (arr.length < FETCH_CHUNK) break;
      }
      setHasLoaded(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load order report.');
      setRows([]);
      setTotal(0);
      setHasLoaded(false);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  };

  const applyModalFilters = () => {
    setAppliedFilters({ ...draft });
    setFilterModalOpen(false);
    setTableSearch('');
    loadReport({ ...draft });
  };

  const cancelModal = () => {
    setDraft({ ...appliedFilters });
    setFilterModalOpen(false);
  };

  const openFilterModal = () => setFilterModalOpen(true);

  const onToolbarRefresh = () => {
    if (!hasLoaded || loading) return;
    setTableSearch('');
    loadReport(appliedFilters);
  };

  const clearModalFilters = () => {
    setDraft(defaultFilters());
  };

  const searchQ = tableSearch.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchQ) return rows;
    return rows.filter((r) => rowMatchesTableSearch(r, searchQ));
  }, [rows, searchQ]);

  const grandNumericSums = useMemo(() => computeNumericSums(filteredRows), [filteredRows]);

  const onExportExcel = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then export.');
      return;
    }
    setExcelExporting(true);
    setError('');
    try {
      const all = filteredRows;
      if (!all.length) {
        setError('No rows to export.');
        return;
      }
      const sheetData = all.map((r, i) => rowToFlat(r, i));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      applyExcelHeaderRowStyle(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Orders');
      const fname = `order-report-${appliedFilters.dateFrom}-${appliedFilters.dateTo}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Excel export failed.');
    } finally {
      setExcelExporting(false);
    }
  };

  const openPrintColumnModal = () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then print.');
      return;
    }
    if (!filteredRows.length) {
      setError('No rows to print.');
      return;
    }
    setError('');
    setPrintColumnSelection(defaultPrintColumnSelection());
    setPrintColumnModalOpen(true);
  };

  /** Same as `openPrintColumnModal` — kept so old bundles / HMR cannot reference an undefined `onPrintPdf`. */
  const onPrintPdf = openPrintColumnModal;

  /** Client-side only: HTML + browser print / Save as PDF (no server print API). */
  const executeOrderReportPrint = async () => {
    const activeCols = PRINT_COLUMN_DEFS.filter((d) => printColumnSelection[d.key]);
    if (!activeCols.length) {
      setError('Select at least one column to print.');
      return;
    }

    setPrintColumnModalOpen(false);
    setPrintPreparing(true);
    setError('');
    try {
      const all = filteredRows;
      if (!all.length) {
        setError('No rows to print.');
        return;
      }

      const esc = (s) =>
        String(s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (e) {
        console.warn('Order report print: no print header', e);
      }

      const headHtml = `<tr>${activeCols.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr>`;
      const bodyHtml = all
        .map((r, i) => {
          const o = rowToFlat(r, i);
          const cells = activeCols.map((c) => o[c.key]);
          return `<tr>${cells.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
        })
        .join('');

      const summary = esc(filterSummary(appliedFilters));
      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header print-repeat-header-cell">${printHeaderHtml}</div>`
        : '';
      const thBg = REPORT_TABLE_HEADER_BG;
      const thColor = REPORT_TABLE_HEADER_TEXT;
      const thBorder = REPORT_TABLE_HEADER_BORDER;
      /** Minimal title so Chrome/Edge print header shows little or no extra title text (browser still may add date). */
      const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>&#x200b;</title>
        <style>
          html,body{margin:0;padding:0}
          body{font-family:system-ui,sans-serif;font-size:10px;color:#111}
          .report-print-inner{box-sizing:border-box;padding:12px}
          .company-print-header{margin-bottom:14px}
          .company-print-header img{max-height:72px}
          .meta{color:#444;margin-bottom:12px;max-width:100%}
          table{border-collapse:collapse;width:100%;table-layout:auto}
          th,td{border:1px solid ${thBorder};padding:3px 4px;word-wrap:break-word;vertical-align:top}
          th{
            background:${thBg} !important;
            color:${thColor};
            font-weight:700;
            -webkit-print-color-adjust:exact;
            print-color-adjust:exact;
          }
          td{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          @media print{
            /* margin:0 keeps Chromium from drawing URL/date/page in the margin band */
            @page{size:auto;margin:0}
            html,body{margin:0 !important;padding:0 !important}
            .report-print-inner{
              padding:14mm 16mm 16mm 16mm !important;
              box-sizing:border-box;
              -webkit-box-decoration-break:clone;
              box-decoration-break:clone;
            }
            thead{display:table-header-group}
            .company-print-header{page-break-inside:avoid}
          }
        </style></head><body>
        <div class="report-print-inner">
        ${headerBlock}
        <div class="meta">${summary}</div>
        <p style="margin:0 0 8px;font-size:10px;color:#666">${all.length} row(s)</p>
        <table><thead>${headHtml}</thead><tbody>${bodyHtml}</tbody></table>
        </div>
        </body></html>`;
      await printHtmlInSameTab(htmlDoc, 'Order report print');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Print failed.');
    } finally {
      setPrintPreparing(false);
    }
  };

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <div className="space-y-4 relative">
      {printColumnModalOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-report-print-columns-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <h3 id="order-report-print-columns-title" className="text-lg font-semibold text-gray-900">
                Columns for print / PDF
              </h3>
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-5 pt-3 text-sm text-gray-600">
              Choose which columns appear in the printable document. Company print header is added at the top automatically.
            </p>
            <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-gray-100">
              <button
                type="button"
                className="text-xs font-medium text-emerald-700 hover:underline"
                onClick={() => setPrintColumnSelection(defaultPrintColumnSelection())}
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                className="text-xs font-medium text-gray-600 hover:underline"
                onClick={() =>
                  setPrintColumnSelection(Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, false])))
                }
              >
                Clear all
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-2">
              {PRINT_COLUMN_DEFS.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!printColumnSelection[col.key]}
                    onChange={() =>
                      setPrintColumnSelection((prev) => ({
                        ...prev,
                        [col.key]: !prev[col.key],
                      }))
                    }
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-800">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void executeOrderReportPrint()}
                disabled={!PRINT_COLUMN_DEFS.some((d) => printColumnSelection[d.key])}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-report-filter-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h3 id="order-report-filter-title" className="text-lg font-semibold text-gray-900">
                Order report filters
              </h3>
              <button
                type="button"
                onClick={cancelModal}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <p className="text-sm text-gray-600">
                <strong>Status</strong> and <strong>delivery areas</strong> are multi-select: hold{' '}
                <kbd className="px-1 py-0.5 text-xs bg-gray-100 border rounded">Ctrl</kbd> (Windows) or{' '}
                <kbd className="px-1 py-0.5 text-xs bg-gray-100 border rounded">⌘</kbd> (Mac) while clicking options.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">From date</span>
                  <input
                    type="date"
                    value={draft.dateFrom}
                    onChange={(e) => updateDraft({ dateFrom: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">To date</span>
                  <input
                    type="date"
                    value={draft.dateTo}
                    onChange={(e) => updateDraft({ dateTo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={draft.isPickupDate}
                    onChange={(e) => updateDraft({ isPickupDate: e.target.checked, isCreatedDate: false })}
                  />
                  Use pickup date for range
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={draft.isCreatedDate}
                    onChange={(e) => updateDraft({ isCreatedDate: e.target.checked, isPickupDate: false })}
                  />
                  Use created date for range
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700">Status (multi)</label>
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => updateDraft({ statusIds: [] })}
                    >
                      Clear
                    </button>
                  </div>
                  <select
                    multiple
                    value={draft.statusIds.map(String)}
                    onChange={(e) => {
                      const v = [...e.target.selectedOptions].map((o) => Number(o.value));
                      updateDraft({ statusIds: v });
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1 text-sm min-h-[140px]"
                    size={8}
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{draft.statusIds.length} selected</p>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700">Delivery areas (multi)</label>
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => updateDraft({ areaIds: [] })}
                    >
                      Clear
                    </button>
                  </div>
                  <select
                    multiple
                    value={draft.areaIds.map(String)}
                    onChange={(e) => {
                      const v = [...e.target.selectedOptions].map((o) => Number(o.value));
                      updateDraft({ areaIds: v });
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1 text-sm min-h-[140px]"
                    size={8}
                  >
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{draft.areaIds.length} selected</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">Keyword</span>
                  <input
                    value={draft.keyword}
                    onChange={(e) => updateDraft({ keyword: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                    placeholder="Client / customer / mobile"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Voucher no</span>
                  <input
                    value={draft.voucherNo}
                    onChange={(e) => updateDraft({ voucherNo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Bill no</span>
                  <input
                    value={draft.billNo}
                    onChange={(e) => updateDraft({ billNo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <div className="flex flex-wrap gap-4 items-center sm:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.isPaid} onChange={(e) => updateDraft({ isPaid: e.target.checked })} />
                    Paid
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isNotPaid}
                      onChange={(e) => updateDraft({ isNotPaid: e.target.checked })}
                    />
                    Not paid
                  </label>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={clearModalFilters}
                className="px-4 py-2 rounded-lg border border-amber-200 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100"
              >
                Clear all filters
              </button>
              <button
                type="button"
                onClick={cancelModal}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyModalFilters}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                Apply filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="page-title text-2xl font-semibold text-gray-900">Order Reports</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={onToolbarRefresh}
            disabled={loading || !hasLoaded}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={excelExporting || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {excelExporting ? 'Exporting…' : 'Excel'}
          </button>
          <button
            type="button"
            onClick={onPrintPdf}
            disabled={printPreparing || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
            title="Choose columns, then print or Save as PDF in this tab (no new tab, no server print)"
          >
            <Printer className="w-4 h-4" />
            {printPreparing ? 'Preparing…' : 'Print / PDF'}
          </button>
        </div>
      </div>

      {hasLoaded && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Active filters: {filterSummary(appliedFilters)}
        </p>
      )}

      {(hasLoaded || loading) && (
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            disabled={loading && rows.length === 0}
            placeholder="Search loaded rows (voucher, bill, client, customer, mobile, status…)"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-400"
            aria-label="Search in table"
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm">{error}</div>
      )}

      {!hasLoaded && !loading && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-gray-600 text-sm">
          Use <strong>Apply filters</strong> in the dialog to load the report (dialog opens automatically when you open this page).
        </div>
      )}

      <div className="flex max-h-[75vh] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading && loadProgress != null && (
          <div className="border-b border-gray-100 bg-white px-3 pb-2 pt-3">
            <div className="mb-1.5 flex justify-between text-xs text-gray-600">
              <span className="font-medium text-gray-800">Loading pages automatically</span>
              <span className="tabular-nums text-gray-700">
                {loadProgress.total > 0
                  ? `${loadProgress.loaded} / ${loadProgress.total} rows`
                  : rows.length > 0
                    ? `${rows.length} rows…`
                    : 'Connecting…'}
              </span>
            </div>
            {loadProgress.total > 0 ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.min(100, (loadProgress.loaded / Math.max(loadProgress.total, 1)) * 100)}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-emerald-400" />
              </div>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-[1200px] w-full text-left text-xs">
            <thead className="sticky top-0 z-10 border-b border-emerald-300 bg-emerald-100">
              <tr>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">#</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Voucher</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Client bill</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Delivery</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Created</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Product</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Del. chg</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Tax</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Cust amt</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Client amt</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Pay type</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Qty</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Client</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Client mob</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Customer</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Cust mob</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Status</th>
                <th className="min-w-[8rem] px-2 py-2 font-semibold text-emerald-900">Remarks</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Pay ref</th>
                <th className="whitespace-nowrap px-2 py-2 font-semibold text-emerald-900">Return ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading &&
                rows.length === 0 &&
                Array.from({ length: 12 }).map((_, si) => (
                  <tr key={`sk-${si}`} className="bg-white">
                    {Array.from({ length: 20 }).map((_, ci) => (
                      <td key={ci} className="px-2 py-2.5">
                        <div
                          className="h-3 rounded bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-pulse"
                          style={{ width: ci === 0 ? '1.5rem' : `${40 + ((si + ci * 3) % 5) * 12}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              {filteredRows.map((r, i) => {
                const client = r.Client ?? r.client;
                const customer = r.Customer ?? r.customer;
                const status = r.Status ?? r.status;
                const rk = `${r.Id ?? r.id ?? 'row'}-${i}`;
                return (
                  <tr key={rk} className="hover:bg-gray-50">
                    <td className="px-2 py-2 tabular-nums text-gray-600">{i + 1}</td>
                    <td className="whitespace-nowrap px-2 py-2 font-medium text-gray-900">{r.VoucherNo ?? r.voucherNo ?? '—'}</td>
                    <td className="whitespace-nowrap px-2 py-2">{r.ClientBillNo ?? r.clientBillNo ?? '—'}</td>
                    <td className="whitespace-nowrap px-2 py-2">{formatDate(r.DeliveryDate ?? r.deliveryDate)}</td>
                    <td className="whitespace-nowrap px-2 py-2">{formatDate(r.Date ?? r.date)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(r.ProductPrice ?? r.productPrice)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(r.DeliveryCharge ?? r.deliveryCharge)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(r.TAX ?? r.tax)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(r.CustomerAmount ?? r.customerAmount)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(r.ClientAmount ?? r.clientAmount)}</td>
                    <td className="whitespace-nowrap px-2 py-2">{r.PayType ?? r.payType ?? '—'}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.Quantity ?? r.quantity ?? '—'}</td>
                    <td className="max-w-[8rem] truncate px-2 py-2" title={client?.Name ?? client?.name}>
                      {client?.Name ?? client?.name ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{client?.MobileNo1 ?? client?.mobileNo1 ?? '—'}</td>
                    <td className="max-w-[8rem] truncate px-2 py-2" title={customer?.Name ?? customer?.name}>
                      {customer?.Name ?? customer?.name ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{customer?.MobileNo1 ?? customer?.mobileNo1 ?? '—'}</td>
                    <td className="whitespace-nowrap px-2 py-2">{status?.Name ?? status?.name ?? '—'}</td>
                    <td className="max-w-[10rem] truncate px-2 py-2" title={r.Remarks ?? r.remarks}>
                      {r.Remarks ?? r.remarks ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{r.PaymentRefNo ?? r.paymentRefNo ?? '—'}</td>
                    <td className="whitespace-nowrap px-2 py-2">{r.ReturnRefNo ?? r.returnRefNo ?? '—'}</td>
                  </tr>
                );
              })}
              {hasLoaded && !loading && filteredRows.length > 0 && (
                <tr className="border-t-2 border-emerald-400 bg-emerald-100/95 font-semibold text-emerald-950">
                  <td colSpan={5} className="whitespace-nowrap px-2 py-2 text-right text-[11px] uppercase tracking-wide">
                    Grand total · {filteredRows.length} order{filteredRows.length === 1 ? '' : 's'}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs">{num(grandNumericSums.ProductPrice)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs">{num(grandNumericSums.DeliveryCharge)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs">{num(grandNumericSums.TAX)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs">{num(grandNumericSums.CustomerAmount)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs">{num(grandNumericSums.ClientAmount)}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-emerald-800/70">{'\u00a0'}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs">
                    {formatSumQuantity(grandNumericSums.Quantity)}
                  </td>
                  <td colSpan={8} className="px-2 py-2 text-xs text-emerald-800/70">
                    {'\u00a0'}
                  </td>
                </tr>
              )}
              {loading &&
                rows.length > 0 &&
                Array.from({ length: 4 }).map((_, si) => (
                  <tr key={`skt-${si}`} className="bg-emerald-50/30">
                    {Array.from({ length: 20 }).map((_, ci) => (
                      <td key={ci} className="px-2 py-2">
                        <div className="h-2.5 w-[85%] rounded bg-gray-200 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              {!loading && hasLoaded && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={20} className="px-3 py-10 text-center text-gray-500">
                    {rows.length === 0 ? 'No orders for this filter.' : 'No rows match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span>
            {!hasLoaded && !loading && '—'}
            {loading && `${rows.length} loaded${total ? ` of ${total}` : ''}${searchQ ? ` · ${filteredRows.length} match search` : ''}`}
            {hasLoaded &&
              !loading &&
              (searchQ
                ? `${filteredRows.length} shown (search) · ${rows.length} loaded · ${total} from server`
                : `${rows.length} orders in list (${total} from server)`)}
          </span>
        </div>
      </div>
    </div>
  );
}
